import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	type LoginFormValues,
	loginSchema,
} from "@/features/auth/schemas/login-schema";

export interface LoginFormProps {
	error?: string;
	errorAction?: ReactNode;
	loading?: boolean;
	onSubmit?: (values: LoginFormValues) => void;
	onForgotPassword?: () => void;
}

export function LoginForm({
	error,
	errorAction,
	loading = false,
	onSubmit,
	onForgotPassword,
}: LoginFormProps) {
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<LoginFormValues>({ resolver: zodResolver(loginSchema) });
	const [showPassword, setShowPassword] = useState(false);

	return (
		<form
			noValidate
			className="login-form"
			onSubmit={handleSubmit((values) => onSubmit?.(values))}
		>
			<div className="login-form__heading">
				<h1 className="login-form__title">Acesse sua conta</h1>
				<p className="login-form__subtitle">
					Entre para acompanhar suas obras e o diário de campo.
				</p>
			</div>

			{!!error && (
				<p role="alert" className="form-error-alert">
					{error}
				</p>
			)}

			{errorAction}

			<div className="form-group">
				<Label htmlFor="email">E-mail</Label>
				<Input
					id="email"
					type="email"
					placeholder="voce@empresa.com.br"
					autoComplete="email"
					aria-invalid={!!errors.email}
					{...register("email")}
				/>
				{errors.email && <p className="form-error">{errors.email.message}</p>}
			</div>

			<div className="form-group">
				<div className="login-form__password-header">
					<Label htmlFor="password">Senha</Label>
					<button
						type="button"
						onClick={onForgotPassword}
						className="login-form__forgot"
					>
						Esqueceu sua senha?
					</button>
				</div>
				<div className="password-field">
					<Input
						id="password"
						type={showPassword ? "text" : "password"}
						placeholder="••••••••"
						autoComplete="current-password"
						aria-invalid={!!errors.password}
						className="password-field__input"
						{...register("password")}
					/>
					<button
						type="button"
						onClick={() => setShowPassword((value) => !value)}
						aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
						className="password-field__reveal"
					>
						{showPassword ? <EyeOff /> : <Eye />}
					</button>
				</div>
				{errors.password && (
					<p className="form-error">{errors.password.message}</p>
				)}
			</div>

			<Button type="submit" className="login-form__submit" disabled={loading}>
				{loading && <Loader2 className="auth-spinner" />}
				Acessar
			</Button>
		</form>
	);
}
